import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

// A page counts as the current one once its top passes this fraction of the viewer's height
const CURRENT_PAGE_THRESHOLD = 0.3;
// Room left between the pagination bar and a page when jumping to it, so its edge and shadow stay visible
const PAGE_SCROLL_GAP = 8;

const getPageElement = (scrollElement, pageNumber) =>
    scrollElement.querySelector(`.react-pdf__Page[data-page-number="${pageNumber}"]`);

/** Page the viewer is showing: the last one whose top is above the threshold line. */
const findCurrentPage = (scrollElement, numPages) => {
    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    // Short last pages never reach the threshold line: at the end of the scroll the last page is the current one
    if (scrollTop > 0 && scrollTop + clientHeight >= scrollHeight - 1) return numPages;

    const thresholdY = scrollElement.getBoundingClientRect().top + clientHeight * CURRENT_PAGE_THRESHOLD;
    let current = 1;
    for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
        const page = getPageElement(scrollElement, pageNumber);
        if (!page || page.getBoundingClientRect().top > thresholdY) break;
        current = pageNumber;
    }
    return current;
};

/**
 * Page navigation for the PDF viewer. Every page is still rendered: this only reads and moves the
 * viewer's scroll, so scrolling by hand and using these controls always agree.
 */
const PdfPagination = ({ scrollElement, numPages, layoutKey }) => {
    const [currentPage, setCurrentPage] = useState(1);
    // What the user is typing in the page box; null while it just mirrors the current page
    const [draftPage, setDraftPage] = useState(null);
    // The bar floats over the top of the viewer: a page jumped to lands just below it
    const barRef = useRef(null);

    useEffect(() => {
        if (!scrollElement || !numPages) return undefined;

        let frame = null;
        const update = () => {
            frame = null;
            setCurrentPage(findCurrentPage(scrollElement, numPages));
        };
        const onScroll = () => {
            if (frame === null) frame = requestAnimationFrame(update);
        };

        update();
        scrollElement.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            scrollElement.removeEventListener("scroll", onScroll);
            if (frame !== null) cancelAnimationFrame(frame);
        };
    }, [scrollElement, numPages, layoutKey]);

    const goToPage = useCallback(
        (pageNumber) => {
            if (!scrollElement || !numPages) return;
            const target = Math.min(Math.max(1, pageNumber), numPages);
            const page = getPageElement(scrollElement, target);
            if (!page) return;

            const offset = page.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top;
            const barBottom = barRef.current ? barRef.current.offsetTop + barRef.current.offsetHeight : 0;
            scrollElement.scrollTo({ top: scrollElement.scrollTop + offset - barBottom - PAGE_SCROLL_GAP });
            // The scroll event may not fire (already there, or the end of the scroll): reflect the target right away
            setCurrentPage(target);
        },
        [scrollElement, numPages]
    );

    if (!numPages) return null;

    const commitDraft = () => {
        const pageNumber = parseInt(draftPage, 10);
        setDraftPage(null);
        if (!Number.isNaN(pageNumber)) goToPage(pageNumber);
    };

    const onDraftKeyDown = (event) => {
        if (event.key === "Enter") {
            // The viewer is a <form>: Enter here must jump to the page, not submit the form
            event.preventDefault();
            commitDraft();
        } else if (event.key === "Escape") {
            setDraftPage(null);
        }
    };

    const isFirst = currentPage <= 1;
    const isLast = currentPage >= numPages;

    return (
        <nav ref={barRef} className="pdf-pagination" aria-label="PDF pages">
            <button type="button" title="First page" disabled={isFirst} onClick={() => goToPage(1)}>
                <ChevronsLeft size={18} />
            </button>
            <button type="button" title="Previous page" disabled={isFirst} onClick={() => goToPage(currentPage - 1)}>
                <ChevronLeft size={18} />
            </button>
            <label className="pdf-pagination__page">
                <input
                    type="text"
                    inputMode="numeric"
                    aria-label="Page number"
                    value={draftPage ?? String(currentPage)}
                    onFocus={(event) => event.target.select()}
                    onChange={(event) => setDraftPage(event.target.value.replace(/\D/g, ""))}
                    onKeyDown={onDraftKeyDown}
                    onBlur={commitDraft}
                />
                <span>/ {numPages}</span>
            </label>
            <button type="button" title="Next page" disabled={isLast} onClick={() => goToPage(currentPage + 1)}>
                <ChevronRight size={18} />
            </button>
            <button type="button" title="Last page" disabled={isLast} onClick={() => goToPage(numPages)}>
                <ChevronsRight size={18} />
            </button>
        </nav>
    );
};

export default PdfPagination;
